import type { User } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
  type Query,
  type Timestamp,
  type Unsubscribe
} from "firebase/firestore";
import type { Asset, CompanyTransferRequest } from "../types/imobi";
import { db } from "./firebase";

export type CreateCompanyTransferInput = {
  originCompanyId: string;
  originCompanyName: string;
  destinationCompanyId: string;
  destinationCompanyName: string;
  destinationCnpj: string;
  assets: Asset[];
  reason: string;
  transferType: CompanyTransferRequest["transferType"];
};

function serializeTimestamp(value: unknown): string {
  if (value && typeof value === "object" && "toDate" in value) {
    return (value as Timestamp).toDate().toISOString();
  }

  return typeof value === "string" ? value : "";
}

function userName(user: User) {
  return user.displayName ?? user.email ?? user.uid;
}

function mapCompanyTransfer(id: string, data: Record<string, unknown>): CompanyTransferRequest {
  return {
    id,
    originCompanyId: String(data.originCompanyId ?? ""),
    originCompanyName: String(data.originCompanyName ?? ""),
    destinationCompanyId: String(data.destinationCompanyId ?? ""),
    destinationCompanyName: String(data.destinationCompanyName ?? ""),
    destinationCnpj: String(data.destinationCnpj ?? ""),
    assetIds: Array.isArray(data.assetIds) ? data.assetIds.map(String) : [],
    assetPatrimonyNumbers: Array.isArray(data.assetPatrimonyNumbers) ? data.assetPatrimonyNumbers.map(String) : [],
    reason: String(data.reason ?? ""),
    transferType: (data.transferType ?? "sem_nota") as CompanyTransferRequest["transferType"],
    status: (data.status ?? "aguardando_master") as CompanyTransferRequest["status"],
    requestedBy: String(data.requestedBy ?? ""),
    requestedByName: String(data.requestedByName ?? ""),
    approvedBy: data.approvedBy ? String(data.approvedBy) : undefined,
    approvedByName: data.approvedByName ? String(data.approvedByName) : undefined,
    approvedAt: serializeTimestamp(data.approvedAt),
    rejectedBy: data.rejectedBy ? String(data.rejectedBy) : undefined,
    rejectedByName: data.rejectedByName ? String(data.rejectedByName) : undefined,
    rejectedAt: serializeTimestamp(data.rejectedAt),
    createdAt: serializeTimestamp(data.createdAt),
    createdBy: String(data.createdBy ?? ""),
    updatedAt: serializeTimestamp(data.updatedAt),
    updatedBy: data.updatedBy ? String(data.updatedBy) : undefined
  };
}

function subscribeTransferQueries(queries: Query[], onChange: (requests: CompanyTransferRequest[]) => void, onError: (message: string) => void): Unsubscribe {
  const buckets = new Map<number, CompanyTransferRequest[]>();
  const emit = () => {
    const byId = new Map<string, CompanyTransferRequest>();
    Array.from(buckets.values()).flat().forEach((request) => byId.set(request.id, request));
    onChange(Array.from(byId.values()).sort((left, right) => right.createdAt.localeCompare(left.createdAt)));
  };

  const unsubscribes = queries.map((transferQuery, index) => onSnapshot(
    transferQuery,
    (snapshot) => {
      buckets.set(index, snapshot.docs.map((transferDoc) => mapCompanyTransfer(transferDoc.id, transferDoc.data())));
      emit();
    },
    (error) => onError(error.message)
  ));

  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
}

export function subscribeCompanyTransfers(companyIds: string[], onChange: (requests: CompanyTransferRequest[]) => void, onError: (message: string) => void): Unsubscribe {
  if (!db || !companyIds.length) {
    onChange([]);
    return () => undefined;
  }

  const firestore = db;
  const chunks: string[][] = [];
  const uniqueCompanyIds = Array.from(new Set(companyIds));
  for (let index = 0; index < uniqueCompanyIds.length; index += 10) {
    chunks.push(uniqueCompanyIds.slice(index, index + 10));
  }

  return subscribeTransferQueries(chunks.flatMap((chunk) => [
    query(collection(firestore, "companyTransfers"), where("originCompanyId", "in", chunk)),
    query(collection(firestore, "companyTransfers"), where("destinationCompanyId", "in", chunk))
  ]), onChange, onError);
}

export async function createCompanyTransferRequest(input: CreateCompanyTransferInput, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }
  if (!input.assets.length) {
    throw new Error("Selecione pelo menos um bem.");
  }
  if (input.assets.some((asset) => asset.companyId !== input.originCompanyId)) {
    throw new Error("Selecione bens de uma unica empresa de origem.");
  }
  if (input.originCompanyId === input.destinationCompanyId) {
    throw new Error("A empresa de destino precisa ser diferente da origem.");
  }

  const requestRef = doc(collection(db, "companyTransfers"));
  await writeBatch(db)
    .set(requestRef, {
      originCompanyId: input.originCompanyId,
      originCompanyName: input.originCompanyName,
      destinationCompanyId: input.destinationCompanyId,
      destinationCompanyName: input.destinationCompanyName,
      destinationCnpj: input.destinationCnpj,
      assetIds: input.assets.map((asset) => asset.id),
      assetPatrimonyNumbers: input.assets.map((asset) => asset.patrimonyNumber),
      reason: input.reason,
      transferType: input.transferType,
      status: "aguardando_master",
      requestedBy: user.uid,
      requestedByName: userName(user),
      createdAt: serverTimestamp(),
      createdBy: user.uid,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    })
    .commit();
}

export async function approveCompanyTransfer(request: CompanyTransferRequest, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  const transferRef = doc(db, "companyTransfers", request.id);
  const transferSnapshot = await getDoc(transferRef);
  if (!transferSnapshot.exists() || transferSnapshot.data().status !== "aguardando_master") {
    throw new Error("Esta transferencia nao esta mais pendente.");
  }

  const batch = writeBatch(db);
  for (const assetId of request.assetIds) {
    const originAssetRef = doc(db, "companies", request.originCompanyId, "assets", assetId);
    const originAssetSnapshot = await getDoc(originAssetRef);
    if (!originAssetSnapshot.exists()) {
      continue;
    }

    const originAsset = originAssetSnapshot.data();
    const destinationAssetRef = doc(db, "companies", request.destinationCompanyId, "assets", assetId);
    batch.set(destinationAssetRef, {
      ...originAsset,
      companyId: request.destinationCompanyId,
      branchId: "matriz",
      status: "ativo",
      acquisitionCondition: "transferencia",
      transferOriginCompanyId: request.originCompanyId,
      transferOriginCompanyName: request.originCompanyName,
      transferRequestId: request.id,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    }, { merge: true });
    batch.update(originAssetRef, {
      status: "baixado",
      transferDestinationCompanyId: request.destinationCompanyId,
      transferDestinationCompanyName: request.destinationCompanyName,
      transferRequestId: request.id,
      updatedAt: serverTimestamp(),
      updatedBy: user.uid
    });
  }

  batch.update(transferRef, {
    status: "aprovada",
    approvedBy: user.uid,
    approvedByName: userName(user),
    approvedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
  await batch.commit();
}

export async function rejectCompanyTransfer(requestId: string, user: User): Promise<void> {
  if (!db) {
    throw new Error("Firestore nao configurado.");
  }

  await updateDoc(doc(db, "companyTransfers", requestId), {
    status: "recusada",
    rejectedBy: user.uid,
    rejectedByName: userName(user),
    rejectedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    updatedBy: user.uid
  });
}
